import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonViewWillEnter,
} from '@ionic/react'
import React, { BaseSyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { getImmobilizationSelector } from '../selectors'
import { chevronBackOutline } from 'ionicons/icons'
import {
  saveImmobilization,
  setOption,
  setDevicesUsed,
  setAssessment,
  setSite,
  setUpdateInputRows,
  setSize,
} from '../store/immobilization.actions'
import { getImmobilizationOptionsSelector } from '../../../options/selectors'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import { getCurrentDate, getCurrentTime, randomNumber } from '../../../../utilities'
import { ImmobilizationConstants } from '../store/immobilization.constants'
import { ProcedureConstants } from '../../../../components/procedure/store/procedure.constants'
import { setInputRows } from '../../../../components/procedure/store/procedure.actions'
import Procedure from '../../../../components/procedure/Procedure.component'
import PCRHeader from '../../../../components/pcrHeader/PCRHeader.component'
import { IOption } from '../../../../common/common.types'
import { showAlert } from '../../../../components/alert/store/alert.actions'
import { compareObjectsIfEqual, filterOutObjectKeys } from '../../../../utilities/object.util'
import { initialImmobilizationState } from '../store/immobilization.reducers'
import { setDeleteProcedure, setProcedureLoaderItem } from '../../../timeline/store/timeline.actions'
import { TimelineConstants } from '../../../timeline/store/timeline.constants'
import { goBack } from '../../../../utilities/navigation.util'
import { IServiceConfig } from '../../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../../configurations/selectors/serviceConfig.selector'
import { IAlertState } from '../../../../components/alert/store/alert.types'

const Immobilization: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const immobilization = useSelector(getImmobilizationSelector)
  const {
    date,
    time,
    assessedBy,
    successfulAttempt,
    pta,
    option,
    devicesUsed,
    assessment,
    site,
    procedureComplications,
    response,
    responsePNNV,
    authorization,
    comments,
    updateInputRows,
    attempts,
    size,
    rolePerforming,
    rolePerformingPNNV,
    isEkgImport,
    procedurePerformedByAnotherUnit,
    procedurePerformedByAnotherUnitPNNV,
  } = immobilization
  const immobilizationOptions = useSelector(getImmobilizationOptionsSelector)
  const { assessmentOptions, siteOptions, optionOptions, devicesUsedOptions } = immobilizationOptions
  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = !!DISPLAY_SECONDS
  const save = () => {
    dispatch(saveImmobilization())
  }

  const optionOnChange = (option: IOption) => {
    dispatch(setOption(option))
    save()
  }

  const dateTimeValidationCheck = () => {
    if (!date || !time) {
      dispatch(showAlert({ header: '', message: 'Date / Time is required' }))
      return false
    }
    return true
  }

  const initialStateCheck = () => {
    if (pta && ((date && time) || (!date && !time))) return true
    const fieldsToFilter = ['key', 'addToTimeline', 'updateInputRows']
    const currentState = filterOutObjectKeys(immobilization, fieldsToFilter)
    const initalState = filterOutObjectKeys(initialImmobilizationState, fieldsToFilter)
    if (!compareObjectsIfEqual(currentState, initalState)) {
      return dateTimeValidationCheck()
    }
    return true
  }

  const onSaveClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (!pta && (!date || !time)) {
      dateTimeValidationCheck()
      return
    }
    const dateTimeValidationCheckResult = initialStateCheck()
    if (dateTimeValidationCheckResult) {
      history.push(TabConstants.TIMELINE_TAB)
    }
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    const dateTimeValidationCheck = initialStateCheck()

    if ((!date || !time) && !pta) {
      const alert: IAlertState = {
        message: t('common.saveTimelineEntry'),
        buttons: [
          {
            text: t('common.buttons.Yes'),
            handler: () => {
              const date = getCurrentDate()
              let time = getCurrentTime()
              if (!displaySeconds) {
                time = time.replace(/(:\d+)(:\d+)$/, '$1:00')
              }

              dispatch({ type: 'immobilization/SET_TIME', time: time })
              dispatch({ type: 'immobilization/SET_DATE', date: date })
              save()
              goBack()
            },
          },
          {
            text: t('common.buttons.No'),
            role: 'destructive',
            handler: () => {
              const itemToRemove = {
                key: immobilization.key,
                rowActionType: TimelineConstants.SET_IMMOBILIZATION_PROCEDURE_ITEM,
              }
              dispatch(setDeleteProcedure(true))
              dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: itemToRemove })
              goBack()
            },
          },
        ],
      }
      dispatch(showAlert(alert))
      dispatch(setProcedureLoaderItem(false))
    } else {
      if (dateTimeValidationCheck) {
        goBack()
      }
      dispatch(setProcedureLoaderItem(false))
    }
  }

  const sizeOnChange = (values: string[]) => {
    dispatch(setSize(values.join('')))
    save()
  }

  const inputRows = [
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: ImmobilizationConstants.SET_OPTION,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('immobilization.option'),
            dataTestID: 'Option',
            options: optionOptions,
            action: optionOnChange,
            listHeader: 'Option',
            selectedOption: option,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: ImmobilizationConstants.SET_DEVICES_USED,
          type: ProcedureConstants.MULTI_SELECT,
          hidden: false,
          props: {
            label: t('immobilization.devicesUsed'),
            dataTestID: 'DevicesUsed',
            options: devicesUsedOptions,
            action: setDevicesUsed,
            listHeader: 'Devices Used',
            savedValues: devicesUsed,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: ImmobilizationConstants.SET_ASSESSMENT,
          type: ProcedureConstants.MULTI_SELECT,
          hidden: false,
          props: {
            label: t('immobilization.assessment'),
            dataTestID: 'Assessment',
            options: assessmentOptions,
            action: setAssessment,
            listHeader: 'Assessment',
            savedValues: assessment,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: ImmobilizationConstants.SET_SITE,
          type: ProcedureConstants.MULTI_SELECT,
          hidden: false,
          props: {
            label: t('immobilization.site'),
            dataTestID: 'Site',
            options: siteOptions,
            action: setSite,
            listHeader: 'Site',
            savedValues: site,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: ImmobilizationConstants.SET_SIZE,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: false,
          props: {
            label: t('immobilization.size'),
            dataTestId: 'Size',
            elementId: 'Size',
            values: size.split(''),
            onNumPadClick: sizeOnChange,
          },
        },
      ],
    },
  ]

  useIonViewWillEnter(() => {
    dispatch(setUpdateInputRows(true))
    return
  })

  if (updateInputRows) {
    dispatch(setInputRows(inputRows))
    dispatch(setUpdateInputRows(false))
  }

  return (
    <IonPage id='ImmobilizationPage'>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' fill='clear' onClick={onSaveClick} color='primary-alt'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle>Immobilization</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen color='light'>
        <Procedure
          type={'immobilization'}
          procedureDate={date}
          procedureTime={time}
          assessedBy={assessedBy}
          successfulAttempt={successfulAttempt}
          procedureComplications={procedureComplications}
          response={response}
          responsePNNV={responsePNNV}
          authorization={authorization}
          comments={comments}
          attempts={attempts}
          hideAttemptField={true}
          hideSuccessfulAttempt={true}
          rolePerforming={rolePerforming}
          rolePerformingPNNV={rolePerformingPNNV}
          pta={pta}
          isEkgImport={isEkgImport}
          procedurePerformedByAnotherUnit={procedurePerformedByAnotherUnit}
          procedurePerformedByAnotherUnitPNNV={procedurePerformedByAnotherUnitPNNV}
        />
      </IonContent>
    </IonPage>
  )
}

export default Immobilization
